export type VivaErrorCode =
  | 'network'
  | 'timeout'
  | 'http'
  | 'bad-credentials'
  | 'account-expired'
  | 'account-disabled'
  | 'empty-playlist'
  | 'parse'
  | 'invalid-input'
  | 'storage'
  | 'cancelled';

/**
 * Typed failure surfaced to the UI. `code` selects the user-facing message,
 * `detail` carries safe diagnostic context (never credentials).
 */
export class VivaError extends Error {
  readonly code: VivaErrorCode;
  readonly detail: string | undefined;
  readonly status: number | undefined;

  constructor(code: VivaErrorCode, message: string, options: { detail?: string; status?: number; cause?: unknown } = {}) {
    super(message);
    this.name = 'VivaError';
    this.code = code;
    this.detail = options.detail;
    this.status = options.status;
    if (options.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

export function isVivaError(value: unknown): value is VivaError {
  return value instanceof VivaError || (typeof value === 'object' && value !== null && (value as { name?: unknown }).name === 'VivaError');
}

/** Serializable error shape for crossing worker / bridge boundaries. */
export interface SerializedError {
  code: VivaErrorCode;
  message: string;
  detail?: string;
  status?: number;
}

export function serializeError(error: unknown): SerializedError {
  if (isVivaError(error)) {
    return { code: error.code, message: error.message, detail: error.detail, status: error.status };
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return { code: 'cancelled', message: 'Operation cancelled' };
  }
  if (error instanceof Error && (error.name === 'QuotaExceededError' || error.name === 'DataError' || error.name === 'InvalidStateError')) {
    return { code: 'storage', message: 'Local storage failed', detail: error.name };
  }
  return { code: 'storage', message: error instanceof Error ? error.message : String(error) };
}

export function deserializeError(value: SerializedError): VivaError {
  return new VivaError(value.code, value.message, { detail: value.detail, status: value.status });
}

/** Maps an error code to a message suitable for end users. */
export function userMessageFor(code: VivaErrorCode): string {
  switch (code) {
    case 'network':
      return 'Cannot reach the server. Check your internet connection and the address.';
    case 'timeout':
      return 'The server took too long to respond. Try again later.';
    case 'http':
      return 'The server returned an error.';
    case 'bad-credentials':
      return 'Username or password is incorrect.';
    case 'account-expired':
      return 'This account has expired. Contact your provider.';
    case 'account-disabled':
      return 'This account is disabled or banned. Contact your provider.';
    case 'empty-playlist':
      return 'The playlist contains no channels or videos.';
    case 'parse':
      return 'The playlist could not be read. It may be damaged or not a playlist.';
    case 'invalid-input':
      return 'Some details are missing or invalid.';
    case 'storage':
      return 'Saving data on this device failed. Free up space and try again.';
    case 'cancelled':
      return 'Cancelled.';
  }
}
